import type {
  CharacteristicFilterType,
  CharacteristicFilterWithRange,
  CharacteristicOptionType,
  EntityFilterType,
  StoreShortWithSupplierType,
} from '@/types'
import { trimTrailingZeros } from '@/utils/format/trimTrailingZeros'
import {
  formatSlottedValues,
  isSlottedCharacteristicType,
  looksLikeSlottedValues,
} from '@/utils/search/characteristics/slottedCharacteristics'

export function normalizeSearchText(value: string): string {
  return value.trim().toLowerCase()
}

export function textMatchesQuery(text: string, query: string): boolean {
  const normalizedQuery = normalizeSearchText(query)
  if (!normalizedQuery) return true
  return normalizeSearchText(text).includes(normalizedQuery)
}

export function splitByMatch(
  text: string,
  query: string
): { text: string; matched: boolean }[] {
  const normalizedQuery = normalizeSearchText(query)
  if (!normalizedQuery) return [{ text, matched: false }]

  const matchIndex = text.toLowerCase().indexOf(normalizedQuery)
  if (matchIndex === -1) return [{ text, matched: false }]

  const segments: { text: string; matched: boolean }[] = []
  if (matchIndex > 0) {
    segments.push({ text: text.slice(0, matchIndex), matched: false })
  }
  segments.push({
    text: text.slice(matchIndex, matchIndex + normalizedQuery.length),
    matched: true,
  })
  if (matchIndex + normalizedQuery.length < text.length) {
    segments.push({
      text: text.slice(matchIndex + normalizedQuery.length),
      matched: false,
    })
  }
  return segments
}

export function sectionMatchesSearch(
  title: string,
  optionLabels: string[],
  query: string
): boolean {
  if (!query.trim()) return true
  if (textMatchesQuery(title, query)) return true
  return optionLabels.some((label) => textMatchesQuery(label, query))
}

export const PILL_RENDER_MAX_OPTIONS = 6

export function shouldRenderAsPills(optionCount: number): boolean {
  return optionCount > 0 && optionCount <= PILL_RENDER_MAX_OPTIONS
}

export function formatCharacteristicValue(
  value: string,
  locale: string = typeof navigator !== 'undefined' ? navigator.language : 'ru'
): string {
  const stringValue = String(value)

  if (stringValue === 'true') return locale.startsWith('ru') ? 'Да' : 'True'
  if (stringValue === 'false') return locale.startsWith('ru') ? 'Нет' : 'False'

  const isStrictNumber =
    !isNaN(Number(stringValue.replace(',', '.'))) && stringValue.trim() !== ''
  if (isStrictNumber) {
    return trimTrailingZeros(stringValue)
  }

  return stringValue
}

export function toggleCharacteristicOptionValue(
  selectedCharacteristics: CharacteristicFilterType[],
  item: CharacteristicFilterType,
  value: string
): CharacteristicFilterType[] {
  const selectedChar = selectedCharacteristics.find(
    (sel) => sel.code === item.code
  )

  const currentValues = selectedChar
    ? Array.from(
        new Set([
          ...(selectedChar.options?.map((o) => String(o.value)) || []),
          ...(selectedChar.chosenOptions || []),
        ])
      )
    : []

  const isAlreadySelected = currentValues.includes(String(value))
  const newValues = isAlreadySelected
    ? currentValues.filter((v) => v !== String(value))
    : [...currentValues, String(value)]

  if (newValues.length === 0) {
    return selectedCharacteristics.filter((sel) => sel.code !== item.code)
  }

  const updatedChar: CharacteristicFilterType = {
    ...item,
    options: newValues.map((v) => ({ value: v, count: 0 })),
    chosenOptions: [],
  }

  if (!selectedChar) {
    return [...selectedCharacteristics, updatedChar]
  }

  return selectedCharacteristics.map((sel) =>
    sel.code === item.code ? updatedChar : sel
  )
}

export function removeCharacteristicByCode(
  selectedCharacteristics: CharacteristicFilterType[],
  code: number
): CharacteristicFilterType[] {
  return selectedCharacteristics.filter((sel) => sel.code !== code)
}

export type FilterIrrelevance = 'characteristic' | 'value'

export const IRRELEVANT_CHARACTERISTIC_TOOLTIP =
  'Данная характеристика не применима к текущей категории или результатам поиска'

export const IRRELEVANT_VALUE_TOOLTIP =
  'Товары с данным значением отсутствуют в текущей выборке'

export function getIrrelevanceTooltip(irrelevance: FilterIrrelevance): string {
  return irrelevance === 'characteristic'
    ? IRRELEVANT_CHARACTERISTIC_TOOLTIP
    : IRRELEVANT_VALUE_TOOLTIP
}

export type ChipRelevance = {
  isRelevant?: boolean
  irrelevance?: FilterIrrelevance
}

export type FilterChip =
  | ({
      id: string
      kind: 'brand'
      label: string
      entityId: string
    } & ChipRelevance)
  | ({
      id: string
      kind: 'store'
      label: string
      entityId: string
    } & ChipRelevance)
  | ({
      id: string
      kind: 'parent'
      label: string
      entityId: string
    } & ChipRelevance)
  | ({
      id: string
      kind: 'characteristic-option'
      label: string
      characteristicCode: number
      optionValue: string
    } & ChipRelevance)
  | ({
      id: string
      kind: 'characteristic-range'
      label: string
      characteristicCode: number
    } & ChipRelevance)
  | ({
      id: string
      kind: 'characteristic-slotted'
      label: string
      characteristicCode: number
    } & ChipRelevance)

/**
 * Фильтр применим к текущей выборке (п.11 записки): `isRelevant` на самой
 * характеристике и на конкретном значении `chosenOptions` независимы —
 * характеристика может быть релевантна в целом, а отдельное значение внутри
 * неё — нет.
 */
export function isOptionRelevant(
  characteristic: { isRelevant?: boolean; irrelevantOptionValues?: string[] },
  optionValue?: string
): boolean {
  if (characteristic.isRelevant === false) return false
  if (optionValue === undefined) return true
  return !characteristic.irrelevantOptionValues?.includes(optionValue)
}

export function getOptionIrrelevance(
  characteristic: { isRelevant?: boolean; irrelevantOptionValues?: string[] },
  optionValue?: string
): FilterIrrelevance | undefined {
  if (characteristic.isRelevant === false) return 'characteristic'
  if (
    optionValue !== undefined &&
    !isOptionRelevant(characteristic, optionValue)
  ) {
    return 'value'
  }
  return undefined
}

export function getSelectedCharacteristicValues(characteristic: {
  options?: { value: unknown }[]
  chosenOptions?: string[]
}): string[] {
  return Array.from(
    new Set([
      ...(characteristic.options?.map((o) => String(o.value)) || []),
      ...(characteristic.chosenOptions || []),
    ])
  )
}

export function splitValuesByRelevance(characteristic: {
  isRelevant?: boolean
  irrelevantOptionValues?: string[]
  options?: { value: unknown }[]
  chosenOptions?: string[]
}): {
  characteristicIrrelevant: boolean
  relevantValues: string[]
  irrelevantValues: string[]
} {
  const values = getSelectedCharacteristicValues(characteristic)
  if (characteristic.isRelevant === false) {
    return {
      characteristicIrrelevant: true,
      relevantValues: values,
      irrelevantValues: [],
    }
  }
  return {
    characteristicIrrelevant: false,
    relevantValues: values.filter((v) => isOptionRelevant(characteristic, v)),
    irrelevantValues: values.filter(
      (v) => !isOptionRelevant(characteristic, v)
    ),
  }
}

function relevanceFields(
  irrelevance: FilterIrrelevance | undefined
): ChipRelevance {
  return irrelevance ? { isRelevant: false, irrelevance } : { isRelevant: true }
}

export function getCharacteristicRelevance(
  characteristic: Partial<
    Pick<
      CharacteristicFilterType,
      | 'isRelevant'
      | 'irrelevantOptionValues'
      | 'options'
      | 'chosenOptions'
      | 'chosenRange'
    >
  >
): ChipRelevance {
  const { characteristicIrrelevant, relevantValues, irrelevantValues } =
    splitValuesByRelevance(characteristic)
  if (characteristicIrrelevant) return relevanceFields('characteristic')

  const hasRange =
    characteristic.chosenRange?.min != null ||
    characteristic.chosenRange?.max != null
  const hasProducts =
    irrelevantValues.length === 0 || relevantValues.length > 0 || hasRange
  return relevanceFields(hasProducts ? undefined : 'value')
}

export type CharacteristicChipRelevance = {
  isCharacteristicIrrelevant: boolean
  irrelevantValues: string[]
}

export function getCharacteristicChipRelevance(
  characteristic: Partial<
    Pick<
      CharacteristicFilterType,
      'isRelevant' | 'irrelevantOptionValues' | 'options' | 'chosenOptions'
    >
  >
): CharacteristicChipRelevance {
  const { characteristicIrrelevant, irrelevantValues } =
    splitValuesByRelevance(characteristic)
  return {
    isCharacteristicIrrelevant: characteristicIrrelevant,
    irrelevantValues,
  }
}

export function formatRangeLabel(range: {
  min?: number | null
  max?: number | null
}): string {
  return [
    range.min != null ? `от ${range.min}` : '',
    range.max != null ? `до ${range.max}` : '',
  ]
    .filter(Boolean)
    .join(' ')
}

type PinnableOption = CharacteristicOptionType & { description?: string }

export function pinSelectedOptions(
  options: PinnableOption[],
  selectedValues: string[]
): PinnableOption[] {
  const selected = new Set(selectedValues)
  const present = new Set(options.map((option) => String(option.value)))
  const missing = Array.from(selected)
    .filter((value) => !present.has(value))
    .map((value) => ({ value, count: 0 }))

  return [
    ...options.filter((option) => selected.has(String(option.value))),
    ...missing,
    ...options.filter((option) => !selected.has(String(option.value))),
  ]
}

export function buildActiveFilterChips(params: {
  selectedBrands: EntityFilterType[]
  selectedStores: StoreShortWithSupplierType[]
  selectedParents: EntityFilterType[]
  selectedCharacteristics: CharacteristicFilterWithRange[]
  characteristicTypeByCode?: Map<number, string | null | undefined>
}): FilterChip[] {
  const {
    selectedBrands,
    selectedStores,
    selectedParents,
    selectedCharacteristics,
    characteristicTypeByCode,
  } = params
  const chips: FilterChip[] = []

  selectedBrands.forEach((brand) => {
    chips.push({
      id: `brand:${brand.id}`,
      kind: 'brand',
      label: brand.description,
      entityId: brand.id,
    })
  })

  selectedStores.forEach((store) => {
    chips.push({
      id: `store:${store.id}`,
      kind: 'store',
      label: store.description,
      entityId: store.id,
    })
  })

  selectedParents.forEach((parent) => {
    chips.push({
      id: `parent:${parent.id}`,
      kind: 'parent',
      label: parent.description,
      entityId: parent.id,
    })
  })

  selectedCharacteristics.forEach((characteristic) => {
    if (characteristic.chosenRange) {
      const { min, max } = characteristic.chosenRange
      const unit = characteristic.unitOfMeasurementDto?.description
      const rangeLabel = [
        min !== null && min !== undefined ? `от ${min}` : null,
        max !== null && max !== undefined ? `до ${max}` : null,
      ]
        .filter(Boolean)
        .join(' ')

      chips.push({
        id: `char-range:${characteristic.code}`,
        kind: 'characteristic-range',
        label: `${characteristic.name}: ${rangeLabel}${unit ? ` ${unit}` : ''}`,
        characteristicCode: characteristic.code,
        ...relevanceFields(getOptionIrrelevance(characteristic)),
      })
      return
    }

    const values = Array.from(
      new Set([
        ...(characteristic.options?.map((o) => String(o.value)) || []),
        ...(characteristic.chosenOptions || []),
      ])
    )

    const typeIsKnown = characteristicTypeByCode?.has(characteristic.code)
    const resolvedType =
      characteristicTypeByCode?.get(characteristic.code) ?? characteristic.type
    const isSlotted = typeIsKnown
      ? isSlottedCharacteristicType(resolvedType)
      : isSlottedCharacteristicType(characteristic.type) ||
        looksLikeSlottedValues(values)

    if (isSlotted) {
      chips.push({
        id: `char-slotted:${characteristic.code}`,
        kind: 'characteristic-slotted',
        label: `${characteristic.name}: ${formatSlottedValues(values) || values.join(', ')}`,
        characteristicCode: characteristic.code,
        ...getCharacteristicRelevance(characteristic),
      })
      return
    }

    values.forEach((value) => {
      chips.push({
        id: `char:${characteristic.code}:${value}`,
        kind: 'characteristic-option',
        label: `${characteristic.name}: ${formatCharacteristicValue(value)}`,
        characteristicCode: characteristic.code,
        optionValue: value,
        ...relevanceFields(getOptionIrrelevance(characteristic, value)),
      })
    })
  })

  return chips
}

export const sortChosenFirst = <T>(
  items: T[],
  isChosen: (item: T) => boolean
): T[] => [
  ...items.filter((item) => isChosen(item)),
  ...items.filter((item) => !isChosen(item)),
]
